import BaseService from 'services/BaseService';
import { lowerKeys } from 'services/AdLaunchService';
import {
  CheckResult,
  GeoBatchRequest,
  GeoBatchStatus,
  GeoLaunchRow,
  MatrixResponse,
  QueuedGeoBatch,
  SourceAd,
} from 'pages/AdLaunch/geoLaunch';

/**
 * The Geo Launcher API (sbly-dash-collector src/sbly-api-server/ad-launch/geoLaunchController.js).
 *
 * Calls whose failure the operator has to read — a matrix that could not reach
 * a geo account, a batch refused for a wrong link — go through `this.http`
 * directly, so the server's message reaches the page instead of a bare null.
 */
class GeoLaunchService extends BaseService {
  basePath = 'v1/ad-launch/geo';

  /** @param addedIds US ads to show alongside the winners (added by ID). */
  async getMatrix(addedIds: string[] = []) {
    await this._getAccessToken();
    const res = await this.http.get(`${this.basePath}/matrix`, {
      params: addedIds.length ? { added: addedIds.join(',') } : {},
    });
    return res.data as MatrixResponse;
  }

  async getSources(adIds: string[]) {
    await this._getAccessToken();
    const res = await this.http.get(`${this.basePath}/sources`, {
      params: { ids: adIds.join(',') },
    });
    return (res.data as { sources: SourceAd[] }).sources;
  }

  async check(batches: GeoBatchRequest[]) {
    await this._getAccessToken();
    const res = await this.http.post(`${this.basePath}/check`, { batches });
    return (res.data as { results: CheckResult[] }).results;
  }

  async launch(batches: GeoBatchRequest[]) {
    await this._getAccessToken();
    const res = await this.http.post(`${this.basePath}/batches`, { batches });
    return (res.data as { batches: QueuedGeoBatch[] }).batches;
  }

  async getBatchStatus(batchIds: string[]) {
    await this._getAccessToken();
    const res = await this.handleHttpRequest<{
      batches: Record<string, GeoBatchStatus>;
      /** Launch workers listening; null when the server cannot tell. */
      workers: number | null;
    }>(
      () =>
        this.http.get(`${this.basePath}/batches`, {
          params: { ids: batchIds.join(',') },
        }),
      'Failed to load geo launch status',
    );
    if (!res) return res;
    const batches: Record<string, GeoBatchStatus> = {};
    for (const [id, batch] of Object.entries(res.batches)) {
      batches[id] = {
        ...batch,
        launches: batch.launches.map(l => lowerKeys<GeoLaunchRow>(l)),
      };
    }
    return { batches, workers: res.workers };
  }

  async getLaunches(limit = 200) {
    await this._getAccessToken();
    const res = await this.http.get(`${this.basePath}/launches`, {
      params: { limit },
    });
    return (res.data as { launches: object[] }).launches.map(l =>
      lowerKeys<GeoLaunchRow>(l),
    );
  }
}

export default GeoLaunchService;
